import { ColorScheme } from '@shared/services/color-scheme-manager';
import { StoreService } from '../store.service';
import { setLocale, setTheme } from '../slices/settings';
import { createAccount, setDeviceId } from '../slices/account';
import { putExternalCredential, putLocalCredential } from '../slices/credentials';

/** Locale, stored in plaintext. */
const LOCALE = "locale";
/** Color scheme, stored in plaintext. */
const COLOR_SCHEME = "color_scheme";
/** Account state, stored serialized. */
const ACCOUNT_USER_ID = "account_state:user_id";
/** Device ID */
const DEVICE_ID = "idaas_device_id";
/** Credential storage's user ID */
const CREDENTIAL_USER_ID = "user";

const OLD_KEY_COLLECTIONS_EXACT = [
  LOCALE,
  COLOR_SCHEME,
  DEVICE_ID,
  CREDENTIAL_USER_ID,
  // used by skip interaction service
  'skip:steps',
  // used by debug storage service
  'idaas_debug_download_media',
  'idaas_debug_download_frames',
  'idaas_debug_facetracker_ui',
  'idaas_debug_force_audio_recorder_polyfill',
]

const OLD_KEY_COLLECTIONS_STARTSWITH = [
  // Misc stores
  'account_state:',
  'challenge:',
  'register:',
  // Credential store
  'user/',
  'credential/',
  // "Stop! Don't share this with anyone" warnings (we had six of these)
  'STOP!',
]

function parse<T = any>(json: string | undefined): T | undefined {
  if (!json) {
    return undefined;
  }
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

export function collectOldLocalstorageItems() {
  const map = new Map<string, string>();
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    const value = localStorage.getItem(key);

    if (OLD_KEY_COLLECTIONS_EXACT.includes(key)) {
      map.set(key, value);
    }
    if (OLD_KEY_COLLECTIONS_STARTSWITH.find(k => key.startsWith(k))) {
      map.set(key, value);
    }
  }
  return map;
}

export function migrateOldStorage(store: StoreService, oldStorage: Map<string, string>) {
  migrateSettings(store, oldStorage);
  migrateAccount(store, oldStorage);
  migrateWebauthn(store, oldStorage);
};


function migrateSettings(store: StoreService, oldStorage: Map<string,string>) {
  const theme = (oldStorage.get(COLOR_SCHEME) ?? undefined) as ColorScheme | undefined;
  if (theme) {
    store.dispatch(setTheme({ theme }));
  }
  const locale = (oldStorage.get(LOCALE));
  if (locale) {
    store.dispatch(setLocale({ locale }));
  }
}

function migrateAccount(store: StoreService, oldStorage: Map<string,string>) {
  const userId = oldStorage.get(CREDENTIAL_USER_ID) ?? parse(oldStorage.get(ACCOUNT_USER_ID));
  const deviceId = parse(oldStorage.get(DEVICE_ID));
  if (userId) {
    store.dispatch(createAccount({ userId, showWelcome: false }));
  }
  if (deviceId) {
    store.dispatch(setDeviceId({ deviceId }));
  }
}

function migrateWebauthn(store: StoreService, oldStorage: Map<string,string>) {
  const userCredentialMap = new Map<string, string>();

  for (const entry of oldStorage.entries()) {
    const [key, value] = entry;
    const userRegex = /^user\/([\w-]+)\/credential$/;

    const matchedUserId = key.match(userRegex)?.[1];

    if (matchedUserId) {
      userCredentialMap.set(matchedUserId, value);
    }
  }

  for (const entry of userCredentialMap.entries()) {
    const [userId, credentialId] = entry;
    const privateKey = parse(oldStorage.get(`credential/${credentialId}`));
    if (privateKey) {
      store.dispatch(putLocalCredential({ userId, privateKey }));
    } else {
      store.dispatch(putExternalCredential({ userId, credentialId }));
    }
  }
}
